import { useT } from "@transifex/react";
import { HeaderLink, TeacherResponse, UserInfo } from "p6m-p6u";
import phase6TeacherLogo from "../../assets/img/phase6-teachers.svg";
import { cp } from "../../config";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { useLogger } from "../../hooks/useLogger";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import HeaderBurgerMenu from "./HeaderBurgerMenu";
import LoginOrAvatar from "./LoginOrAvatar";

interface AreaHeaderProps {
    currentPath: string;
    basePath: string;
    userInfo: UserInfo;
    teacherInfo?: TeacherResponse;
    avatarUrl: string;
}

// DO NOT DELETE — scroll animation commented out for now, may be re-enabled later
// const _updateFontAndHeaderSizes = (scrollTop: number) => {
//     const header = document.getElementById("main-header");
//     const links = document.getElementById("header-links-container");
//
//     const screenSize = window.innerWidth;
//     const xlBreakpoint = 1280;
//
//     if (scrollTop > headerSizeChangeScrollThreshold) {
//         links?.classList.remove("text-l", "xl:text-header-l", "text-m");
//         if (screenSize >= xlBreakpoint) {
//             links?.classList.add("text-l");
//         } else {
//             links?.classList.add("text-m");
//         }
//         header?.style.setProperty("height", "var(--size-header-s)");
//     } else {
//         links?.classList.remove("text-l", "text-m");
//         if (screenSize >= xlBreakpoint) {
//             links?.classList.add("text-header-l");
//         } else {
//             links?.classList.add("text-l");
//         }
//         header?.style.setProperty("height", "var(--size-header-m)");
//     }
// };

const AreaHeader = (props: AreaHeaderProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    const { logEvent } = useLogger();

    const t = useT();
    const t_alt_phase6Logo = t("Logo phase6 for teachers", {
        _tags: "TeacherArea,Header,ScreenReaderOnly",
    });
    const t_sr_homeButton = t("Click here to return to home", {
        _tags: "TeacherArea,Header,ScreenReaderOnly",
    });
    const t_sr_mainPage = t("Go to phase6 main page", {
        _tags: "TeacherArea,Header,ScreenReaderOnly",
    });

    const t_assistant = t("Assistant", { _tags: "TeacherArea,Header" });
    const t_material = t("Material", { _tags: "TeacherArea,Header" });
    const t_webinar = t("Webinar", { _tags: "TeacherArea,Header" });
    const t_services = t("Services", { _tags: "TeacherArea,Header" });

    const t_myAccount = t("My Account", {
        _tags: "TeacherArea, Header",
    });
    const t_myParentAccount = t("My Parent Account", {
        _tags: "TeacherArea, Header",
    });
    const t_myLearningAccount = t("My Learning Account", {
        _tags: "TeacherArea, Header",
    });

    const accountTranslations = {
        myAccount: t_myAccount,
        myParentAccount: t_myParentAccount,
        myLearningAccount: t_myLearningAccount,
    };

    const headerLinks: HeaderLink[] = [
        {
            text: t_assistant,
            link: `${cp.cfg.REACT_APP_PREFIX ?? cp.cfg.REACT_APP_P6_WEBSITE}/assistent`,
        },
        {
            text: t_material,
            link: `${props.basePath}/unterrichtsmaterial`,
        },
        {
            text: t_webinar,
            link: `${props.basePath}/webinare`,
        },
        {
            text: t_services,
            link: `${props.basePath}/services`,
        },
    ];

    const mainPageUrl = `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}?home`;

    // DO NOT DELETE — scroll animation commented out for now, may be re-enabled later
    // useEffect(() => {
    //     return handleHeaderSizeChange({
    //         callback: _updateFontAndHeaderSizes,
    //         attachToPageLoadEvent: false,
    //     });
    // }, []);

    return (
        <header
            className="border-stone-300 fixed z-50 flex h-[var(--size-header-m)] w-full flex-row items-center justify-center border-b bg-white transition-all duration-300 ease-in-out"
            id="main-header"
        >
            <nav
                className={`relative flex h-full w-full items-center ${getStyleBasedOnBreakpoint(
                    Breakpoint.LG,
                    "justify-center",
                    "justify-between"
                )}`}
            >
                <a
                    className={`flex h-full items-center transition-all duration-300 ease-in-out py-s pl-s ${getStyleBasedOnBreakpoint(
                        Breakpoint.XL,
                        "pr-s",
                        "pr-xs"
                    )}`}
                    id="header-logo"
                    href={`${props.basePath}?home`}
                    aria-label={t_sr_homeButton}
                >
                    <img
                        src={phase6TeacherLogo}
                        alt={t_alt_phase6Logo}
                        width="168"
                        height="56"
                        className="max-h-full w-auto object-contain"
                    />
                </a>

                {/* for screens above 1024, desktop version */}

                <section
                    className={`h-full flex-1 flex flex-row pl-l gap-xl items-center justify-end transition-all duration-300 ease-in-out text-header-l ${getStyleBasedOnBreakpoint(
                        Breakpoint.LG,
                        "flex",
                        "hidden"
                    )}`}
                    id="header-links-container"
                >
                    <a
                        className="text-stone-1000 hover:text-forest-green-800 text-header-icon! flex items-center transition-colors duration-300 ease-in-out"
                        aria-label={t_sr_mainPage}
                        href={mainPageUrl}
                        onClick={() => logEvent("Menu Main Page Clicked", { source: "desktop" })}
                    >
                        <PhaseSixIcon name="house" />
                    </a>

                    <div className="gap-m flex items-center">
                        {headerLinks.map((element) => (
                            <a
                                key={element.text}
                                href={element.link}
                                className={`text-stone-1000 header-nav-link ${
                                    element.link.includes(props.currentPath) ? "header-nav-link-active" : ""
                                }`}
                                id={`${element.text}-headerlink`}
                                data-link-path={element.link}
                            >
                                {element.text}
                            </a>
                        ))}
                    </div>

                    <div className="flex justify-center">
                        <LoginOrAvatar
                            currentPath={props.currentPath}
                            userInfo={props.userInfo}
                            avatarUrl={props.avatarUrl}
                            accountTranslations={accountTranslations}
                        />
                    </div>
                </section>

                {/* for screens below 1024, mobile version */}
                <HeaderBurgerMenu
                    mainPageLinks={headerLinks}
                    mainPageUrl={mainPageUrl}
                    userInfo={props.userInfo}
                    teacherInfo={props.teacherInfo}
                    avatarUrl={props.avatarUrl}
                    currentPath={props.currentPath}
                    accountTranslations={accountTranslations}
                />
            </nav>
        </header>
    );
};

export default AreaHeader;
